import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import LifecycleStageBadge from './LifecycleStageBadge';
import { useUpdateLifecycleOverride } from '@/hooks/maintenance/useUpdateLifecycleOverride';
import { MAINTENANCE_PROJECTS_KEY } from '@/lib/queryKeys/maintenanceKeys';
import {
  LIFECYCLE_STAGE_LABELS,
  type LifecycleStage,
} from '@/constants/lifecycleStages';

interface Props {
  projectId: string;
  stage: LifecycleStage | null | undefined;
  isOverridden: boolean;
  editable: boolean;
}

const OPTIONS: Array<{ value: LifecycleStage | null; label: string }> = [
  { value: null, label: 'Tự động' },
  { value: 'yet_transferred', label: LIFECYCLE_STAGE_LABELS.yet_transferred },
  { value: 'warranty', label: LIFECYCLE_STAGE_LABELS.warranty },
  { value: 'watch', label: LIFECYCLE_STAGE_LABELS.watch },
  { value: 'maintenance', label: LIFECYCLE_STAGE_LABELS.maintenance },
];

export default function InlineStageCell({ projectId, stage, isOverridden, editable }: Props) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const mutation = useUpdateLifecycleOverride();

  if (!editable) {
    return <LifecycleStageBadge stage={stage ?? null} isOverridden={isOverridden} />;
  }

  const handlePick = (next: LifecycleStage | null) => {
    setOpen(false);
    const optimisticStage: LifecycleStage | null = (next ?? stage) ?? null;

    const patchCache = (queryKey: any[]) =>
      qc.getQueriesData({ queryKey }).map(([key, data]) => {
        qc.setQueryData(key, (old: any) => {
          if (!old) return old;
          if (Array.isArray(old?.projects)) {
            return {
              ...old,
              projects: old.projects.map((p: any) =>
                p.id === projectId
                  ? { ...p, lifecycle_stage_override: next, lifecycle_stage: optimisticStage }
                  : p,
              ),
            };
          }
          if (Array.isArray(old)) {
            return old.map((p: any) =>
              p.id === projectId
                ? { ...p, lifecycle_stage_override: next, lifecycle_stage: optimisticStage }
                : p,
            );
          }
          return old;
        });
        return [key, data] as const;
      });

    const snapshots = [
      ...patchCache(['projects-optimized-v2']),
      ...patchCache([MAINTENANCE_PROJECTS_KEY]),
    ];

    mutation.mutate(
      { projectId, override: next },
      {
        onError: () => {
          snapshots.forEach(([k, d]) => qc.setQueryData(k, d));
        },
      },
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="cursor-pointer inline-flex items-center"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Đang lưu…
            </span>
          ) : (
            <LifecycleStageBadge stage={stage ?? null} isOverridden={isOverridden} />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-44 p-1"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        {OPTIONS.map((opt) => {
          const isCurrent =
            (opt.value == null && !isOverridden) ||
            (opt.value != null && isOverridden && stage === opt.value);
          return (
            <Button
              key={opt.value ?? 'auto'}
              variant="ghost"
              size="sm"
              className={cn('w-full justify-start gap-2 h-8', isCurrent && 'font-semibold')}
              onClick={() => handlePick(opt.value)}
            >
              <Check className={cn('h-3 w-3', isCurrent ? 'opacity-100' : 'opacity-0')} />
              {opt.label}
            </Button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
